import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';
import { totalAlertas } from '../utils/alertasVeiculos';

/* ============================================================
   ALERTAS DAS VIATURAS — contagem partilhada

   Segue o mesmo padrão dos registos tardios: a contagem vive acima das rotas,
   porque o indicador da barra lateral tem de estar preenchido em qualquer ecrã
   e não só quando as Viaturas estão abertas.

   A conta é feita aqui e não no servidor porque as regras — um mês para o
   seguro e a inspeção, 2500 km para a revisão — já existem em JavaScript para
   pintar os campos de vermelho no cartão. Reescrevê-las em SQL dava duas
   versões da mesma regra, e o menu acabaria a anunciar um número que o ecrã não
   confirma. A frota é pequena; o custo é um pedido leve.

   Sem polling, como no resto da aplicação: recarrega quando o separador volta a
   ficar visível ou ganha foco.
   ============================================================ */

const AlertasVeiculosContext = createContext({ total: 0, recarregar: () => {}, confirmarTratado: () => {} });

export const useAlertasVeiculos = () => useContext(AlertasVeiculosContext);

export const AlertasVeiculosProvider = ({ children }) => {
    const { isAuthenticated, loading: authLoading, hasPermission } = useAuth();
    const [frota, setFrota] = useState([]);
    const pedidoAtual = useRef(0);
    const total = totalAlertas(frota);
    const confirmarTratado = useCallback((veiculo, campo, referencia) => {
        ++pedidoAtual.current;
        setFrota(anterior => {
            const lista = anterior.some(v => Number(v.id) === Number(veiculo.id)) ? anterior : [...anterior, veiculo];
            return lista.map(v => Number(v.id) === Number(veiculo.id)
                ? { ...v, alertas_tratados: { ...v.alertas_tratados, [campo]: referencia } } : v);
        });
    }, []);

    // Sem isto, as rotas públicas fariam este pedido sem token, e o interceptor
    // trata qualquer 401 como sessão expirada e atira o visitante para o login.
    const podeConsultar = !authLoading && isAuthenticated
        && (typeof hasPermission !== 'function' || hasPermission('veiculos'));

    const buscar = useCallback(async () => {
        if (!podeConsultar) return;
        const pedido = ++pedidoAtual.current;
        try {
            const r = await api.get('/veiculos', { params: { _t: Date.now() } });
            if (pedido === pedidoAtual.current) setFrota(Array.isArray(r.data) ? r.data : []);
        } catch {
            // silencioso de propósito: é um indicador acessório e nunca deve
            // interromper o que o utilizador está a fazer
        }
    }, [podeConsultar]);

    useEffect(() => {
        if (!podeConsultar) { ++pedidoAtual.current; setFrota([]); return; }
        buscar();
        const aoVoltar = () => { if (document.visibilityState === 'visible') buscar(); };
        document.addEventListener('visibilitychange', aoVoltar);
        window.addEventListener('focus', aoVoltar);
        return () => {
            document.removeEventListener('visibilitychange', aoVoltar);
            window.removeEventListener('focus', aoVoltar);
        };
    }, [podeConsultar, buscar]);

    return (
        <AlertasVeiculosContext.Provider value={{ total, recarregar: buscar, confirmarTratado }}>
            {children}
        </AlertasVeiculosContext.Provider>
    );
};
